import { getActiveSubscriptionPlanId } from '../../_helpers/subscription-access';
import { getSubscriptionPlanId, isPlanDowngrade } from '../../_helpers/subscription-plan';
import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../_services/auth.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';

export type PaymentPlanId = 'premium' | 'intelligence' | 'advisory';
export interface SelectedPlanPayload {
  id: PaymentPlanId;
  name: string;
  price: number;
  billingPeriod: string;
}

export const SELECTED_PLAN_STORAGE_KEY = 'selectedPaymentPlan';

/** Adjust if your payment page lives at a different path. */
const PAYMENT_ROUTE = '/cnfrm-payment';

export interface PlanFeature {
  label: string;
  description: string;
  value?: string;
  included: boolean;
}

interface PlanTheme {
  header: string;
  title: string;
  price: string;
  muted: string;
  badge: string;
  selected: string;
  indicatorOn: string;
  indicatorOff: string;
  footerOn: string;
}

export interface PaymentPlan {
  id: PaymentPlanId;
  name: string;
  price: number;
  billingPeriod: string;
  tagline: string;
  badge?: string;
  features: PlanFeature[];
  theme: PlanTheme;
}

/** Features are cumulative: each tier unlocks the first N items. */
const FEATURE_CATALOG: Omit<PlanFeature, 'included'>[] = [
  {
    label: 'Property Access',
    description: 'Private, curated listings',
    value: '300+ properties',
  },
  { label: 'Basic CI Preview', description: 'A glimpse of our insights' },
  {
    label: 'Full CI Intelligence',
    description: 'Market, location & project analysis',
  },
  {
    label: 'Fair Value & Negotiation Guidance',
    description: 'Know the right value. Negotiate better.',
  },
  {
    label: 'Personalised Shortlisting',
    description: 'Curated options based on your needs',
  },
  { label: 'Dedicated Advisor', description: 'A single point of contact' },
  {
    label: 'Negotiation & Closing Support',
    description: 'End-to-end guidance till the deal closes',
  },
];

const featuresUpTo = (count: number): PlanFeature[] =>
  FEATURE_CATALOG.map((feature, index) => ({
    ...feature,
    included: index < count,
  }));

export const PLANS: PaymentPlan[] = [
  {
    id: 'premium',
    name: 'Premium',
    price: 5000,
    billingPeriod: '/ 90 days',
    tagline: 'More listings. More clarity.',
    features: featuresUpTo(2),
    theme: {
      header: 'bg-gradient-to-br from-[#fffcf9] via-[#f3edff] to-[#e5d5ff]',
      title: 'text-[#111111]',
      price: 'text-[#111111]',
      muted: 'text-black/50',
      badge: 'bg-gradient-to-r from-[#8c52ff] to-[#3e1157] text-white',
      selected: 'border-[#8c52ff] ring-4 ring-[#8c52ff]/10 shadow-[0_18px_50px_rgba(0,0,0,0.07)]',
      indicatorOn: 'border-[#8c52ff] bg-[#8c52ff] text-white',
      indicatorOff: 'border-black/20 bg-white text-transparent',
      footerOn: 'theme-btn1 text-white',
    },
  },
  {
    id: 'intelligence',
    name: 'Intelligence',
    price: 19999,
    billingPeriod: '/ 90 days',
    tagline: 'Deeper insights. Better decisions.',
    badge: 'Most popular',
    features: featuresUpTo(4),
    theme: {
      header: 'bg-gradient-to-br from-[#f3edff] via-[#e5d5ff] to-[#d2b8ff]',
      title: 'text-[#111111]',
      price: 'text-[#111111]',
      muted: 'text-black/50',
      badge: 'bg-gradient-to-r from-[#8c52ff] to-[#3e1157] text-white',
      selected: 'border-[#8c52ff] ring-4 ring-[#8c52ff]/10 shadow-[0_18px_50px_rgba(0,0,0,0.07)]',
      indicatorOn: 'border-[#8c52ff] bg-[#8c52ff] text-white',
      indicatorOff: 'border-black/20 bg-white text-transparent',
      footerOn: 'theme-btn1 text-white',
    },
  },
  {
    id: 'advisory',
    name: 'Private Advisory',
    price: 99999,
    billingPeriod: '/ transaction',
    tagline: "A partnership for what's next.",
    features: featuresUpTo(FEATURE_CATALOG.length),
    theme: {
      header: 'bg-gradient-to-br from-[#8c52ff] via-[#6229b0] to-[#3e1157]',
      title: 'text-white',
      price: 'text-white',
      muted: 'text-white/80',
      badge: 'bg-gradient-to-r from-[#8c52ff] to-[#3e1157] text-white',
      selected: 'border-[#8c52ff] ring-4 ring-[#8c52ff]/10 shadow-[0_18px_50px_rgba(0,0,0,0.07)]',
      indicatorOn: 'border-white bg-white text-[#6229b0]',
      indicatorOff: 'border-white/60 bg-white/10 text-transparent',
      footerOn: 'theme-btn1 text-white',
    },
  },
];

export const findPaymentPlan = (id: unknown): PaymentPlan | null =>
  PLANS.find((plan) => plan.id === id) ?? null;

/** Matches the `?plan=` value sent by the pricing page (e.g. "Premium", "Advisory"). */
export const findPlanByQuery = (value: string | null): PaymentPlan | null => {
  const query = value?.trim().toLowerCase();
  if (!query) return null;
  return (
    PLANS.find(
      (plan) => plan.id === query || plan.name.toLowerCase() === query,
    ) ?? null
  );
};

/** Free tier: shown for reference only, not selectable and never sent to the payment page. */
type FreePlan = Pick<
  PaymentPlan,
  'name' | 'price' | 'billingPeriod' | 'tagline' | 'features'
>;

export const FREE_PLAN: FreePlan = {
  name: 'Basic',
  price: 0,
  billingPeriod: '',
  tagline: 'Explore. Discover. Get started.',
  features: FEATURE_CATALOG.map((feature, index) => ({
    ...feature,
    included: index < 2,
    value: index < 2 ? '3 properties' : undefined,
  })),
};

const PRICE_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

@Component({
  selector: 'app-payment-plan',
  imports: [NgTemplateOutlet],
  templateUrl: './payment-plan.component.html',
  styleUrl: './payment-plan.component.scss',
})
export class PaymentPlanComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly user = toSignal(this.auth.user$, { initialValue: null });
  readonly currentPlanId = computed(() =>
    getSubscriptionPlanId(this.user() ?? this.auth.getUser())
      ? getActiveSubscriptionPlanId(this.user() ?? this.auth.getUser()) : null,
  );

  @ViewChildren('planCard') private planCards!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  readonly freePlan = FREE_PLAN;
  readonly plans = PLANS;
  readonly selectedPlanId = signal<PaymentPlanId | null>(null);
  readonly isNavigating = signal(false);
  readonly selectionMessage = signal('');
  readonly expandedPlanId = signal<PaymentPlanId | 'basic' | null>(null);

  readonly selectedPlan = computed(
    () => this.plans.find((plan) => plan.id === this.selectedPlanId()) ?? null,
  );

  constructor() {
    // Pre-select the plan picked on the pricing page: /payment?plan=Premium
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const plan = findPlanByQuery(params.get('plan'));
      if (plan) this.selectPlan(plan.id);
      else {
        try {
          const stored = sessionStorage.getItem(SELECTED_PLAN_STORAGE_KEY);
          const saved = stored ? findPaymentPlan(JSON.parse(stored)?.id) : null;
          if (saved) this.selectPlan(saved.id);
        } catch { /* Session storage may be unavailable. */ }
      }
    });
  }

  isSelected(id: PaymentPlanId): boolean {
    return (this.selectedPlanId() ?? this.currentPlanId()) === id;
  }

  isDowngrade(id: PaymentPlanId): boolean {
    return isPlanDowngrade(this.currentPlanId(), id);
  }

  selectPlan(id: PaymentPlanId): void {
    if (id === this.currentPlanId() || this.isDowngrade(id)) return;
    this.selectionMessage.set('');
    this.selectedPlanId.set(id);
    this.expandedPlanId.set(id);
  }

  toggleDetails(id: PaymentPlanId | 'basic'): void {
    this.expandedPlanId.update(current => current === id ? null : id);
  }

  formatPrice(price: number): string {
    return PRICE_FORMATTER.format(price);
  }

  /** Roving tabindex: only one card is in the tab order, per ARIA radio-group pattern. */
  tabIndexFor(id: PaymentPlanId, index: number): number {
    if (id === this.currentPlanId() || this.isDowngrade(id)) return -1;
    const selected = this.selectedPlanId();
    const firstAvailable = this.plans.find(plan => plan.id !== this.currentPlanId() && !this.isDowngrade(plan.id));
    return (selected ? selected === id : firstAvailable?.id === id) ? 0 : -1;
  }

  onCardKeydown(event: KeyboardEvent, index: number): void {
    const steps: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    };
    const step = steps[event.key];
    if (!step) return;

    event.preventDefault();
    let next = index;
    for (let count = 0; count < this.plans.length; count++) {
      next = (next + step + this.plans.length) % this.plans.length;
      if (this.plans[next].id !== this.currentPlanId() && !this.isDowngrade(this.plans[next].id)) break;
    }
    if (this.plans[next].id === this.currentPlanId() || this.isDowngrade(this.plans[next].id)) return;
    this.selectPlan(this.plans[next].id);
    this.planCards.get(next)?.nativeElement.focus();
  }

  async continue(): Promise<void> {
    const plan = this.selectedPlan();
    if (this.isNavigating()) return;
    if (!plan || plan.id === this.currentPlanId() || this.isDowngrade(plan.id)) {
      this.selectionMessage.set(this.currentPlanId() && this.currentPlanId() !== 'basic'
        ? "Current plan can't be pick again" : 'Please select a plan');
      return;
    }

    const payload: SelectedPlanPayload = {
      id: plan.id,
      name: plan.name,
      price: plan.price,
      billingPeriod: plan.billingPeriod,
    };

    // Router state is lost on refresh, so keep a session copy as a fallback.
    try {
      sessionStorage.setItem(
        SELECTED_PLAN_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch {
      /* storage unavailable (private mode / SSR) – router state still carries the plan */
    }

    this.isNavigating.set(true);
    try {
      if (!this.auth.isLoggedIn()) {
        const navigated = await this.router.navigate(['/login'], {
          queryParams: { returnUrl: `/payment?plan=${encodeURIComponent(plan.id)}` },
        });
        if (!navigated) this.isNavigating.set(false);
        return;
      }
      const navigated = await this.router.navigate([PAYMENT_ROUTE], {
        state: { plan: payload },
        queryParams: { plan: plan.id },
        queryParamsHandling: 'merge',
      });
      if (!navigated) this.isNavigating.set(false);
    } catch {
      this.isNavigating.set(false);
    }
  }
}
